import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

/** The spreadsheet's colour bands, as chips. */
export const SECTIONS = {
  track: { label: "Track", text: "text-sec-track", bg: "bg-sec-track-light", dot: "bg-sec-track" },
  recording: { label: "Recording", text: "text-sec-track", bg: "bg-sec-track-light", dot: "bg-sec-track" },
  release: { label: "Release", text: "text-sec-release", bg: "bg-sec-release-light", dot: "bg-sec-release" },
  codes: { label: "Codes", text: "text-sec-codes", bg: "bg-sec-codes-light", dot: "bg-sec-codes" },
  publishing: { label: "Publishing", text: "text-sec-pub", bg: "bg-sec-pub-light", dot: "bg-sec-pub" },
  mechanical: { label: "Mechanical", text: "text-sec-mech", bg: "bg-sec-mech-light", dot: "bg-sec-mech" },
  pro: { label: "PRO", text: "text-sec-pro", bg: "bg-sec-pro-light", dot: "bg-sec-pro" },
  dist: { label: "Distribution", text: "text-sec-dist", bg: "bg-sec-dist-light", dot: "bg-sec-dist" },
};

export function SectionTag({ k, className }) {
  const s = SECTIONS[k];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 px-2.5 py-[3px] rounded-full font-mono text-[10.5px] tracking-[0.14em] uppercase font-semibold",
        s.text,
        s.bg,
        className
      )}
    >
      <span className={cn("w-1.5 h-1.5 rounded-full", s.dot)} />
      {s.label}
    </span>
  );
}

const SHORT = { BMI: "B", MLC: "M", HFA: "H", MR: "R", PUBD: "P", JAMMS: "J" };

/** Little squares, filled when the work is registered there. */
export function SocietyDots({ regs, keys = ["BMI", "MLC", "HFA", "MR"], size = "md", labels }) {
  const px = size === "sm" ? "w-[18px] h-[18px] text-[9px] rounded-[6px]" : "w-[22px] h-[22px] text-[9.5px] rounded-[7px]";
  return (
    <span className="inline-flex gap-1">
      {keys.map((k) => {
        const st = regs?.[k]?.status || (regs?.[k]?.done ? "registered" : "pending");
        const name = labels?.(k) || k;
        const words = { registered: `registered${regs?.[k]?.date ? ` ${regs[k].date}` : ""}`, submitted: "submitted", rejected: "rejected", na: "doesn't apply", pending: "not yet" }[st];
        return (
          <span
            key={k}
            title={`${name}: ${words}`}
            aria-label={`${name} ${words}`}
            className={cn(
              "inline-flex items-center justify-center font-mono font-semibold",
              px,
              st === "registered"
                ? "bg-sec-mech text-white"
                : st === "submitted"
                  ? "bg-sky-50 text-sky-700 border border-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800"
                  : st === "rejected"
                    ? "bg-rose-50 text-stock-out border border-rose-200 dark:bg-rose-950/40 dark:border-rose-900"
                    : st === "na"
                      ? "bg-transparent text-muted2/60 border border-dashed border-line line-through"
                      : "bg-surface text-muted2 border border-line"
            )}
          >
            {SHORT[k] || k[0]}
          </span>
        );
      })}
    </span>
  );
}

export const WRITER_COLOURS = ["bg-sec-pub", "bg-[#4E9A91]", "bg-[#9CCBC4]", "bg-[#C4E2DD]"];

export function SplitBar({ writers = [], className, tall }) {
  const total = writers.reduce((a, w) => a + (w.share || 0), 0);
  return (
    <div className={cn("flex rounded-full overflow-hidden bg-stock-out/15", tall ? "h-2.5" : "h-1.5", className)} title={`Total ${total}%`}>
      {writers
        .filter((w) => w.share)
        .map((w, i) => (
          <div key={i} className={WRITER_COLOURS[i] || WRITER_COLOURS[3]} style={{ width: `${Math.min(w.share, 100)}%` }} />
        ))}
    </div>
  );
}

export function writerLine(writers = []) {
  if (!writers.length) return "No writers";
  return writers.map((w) => `${w.name.split(" ")[0]} ${w.share == null ? "?" : w.share}%`).join(" · ");
}

export function ReleasedPill({ value }) {
  const map = {
    Yes: ["Released", "text-stock-in bg-emerald-50 dark:bg-emerald-950/40"],
    "Need changes": ["Needs changes", "text-gold bg-gold-light"],
    No: ["Not released", "text-muted2 bg-surface"],
  };
  const [label, cls] = map[value] || ["Not released", "text-muted2 bg-surface"];
  return <span className={cn("inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap", cls)}>{label}</span>;
}

export function Pill({ children, tone = "muted", className }) {
  const tones = {
    muted: "text-muted2 bg-surface border border-line",
    brand: "text-brand bg-brand-light",
    good: "text-stock-in bg-emerald-50 dark:bg-emerald-950/40",
    warn: "text-gold bg-gold-light",
    bad: "text-stock-out bg-rose-50 dark:bg-rose-950/40",
    act: "text-orange-700 bg-orange-50 dark:text-orange-300 dark:bg-orange-950/40",
    dist: "text-sec-dist bg-sec-dist-light",
  };
  return (
    <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap", tones[tone], className)}>
      {children}
    </span>
  );
}

export function IssuePills({ issues = [], max = 2 }) {
  if (!issues.length) return <Pill tone="good">Complete</Pill>;
  const shown = [...issues].sort((a, b) => (a.severity === "bad" ? -1 : 1) - (b.severity === "bad" ? -1 : 1)).slice(0, max);
  return (
    <span className="inline-flex flex-wrap gap-1">
      {shown.map((i) => (
        <Pill key={i.code} tone={i.severity === "bad" ? "bad" : "warn"}>
          {i.text}
        </Pill>
      ))}
      {issues.length > max && <Pill>+{issues.length - max}</Pill>}
    </span>
  );
}

export const STAGE_TONE = {
  Voice: "brand",
  Harmony: "brand",
  Mix: "brand",
  Mastered: "brand",
  Ready: "warn",
  Released: "good",
};

/** A native select that looks like the rest of the app — fast and accessible. */
export function FilterSelect({ value, onChange, options, allLabel, icon: Icon, className, ariaLabel }) {
  return (
    <label className={cn("relative inline-flex items-center h-9 rounded-lg border bg-card text-[13px] shrink-0", value ? "border-brand text-brand" : "border-line text-ink", className)}>
      {Icon && <Icon className="w-4 h-4 absolute left-2.5 text-muted2 pointer-events-none" />}
      <select
        aria-label={ariaLabel || allLabel}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn("appearance-none bg-transparent h-full pr-8 outline-none max-w-[190px] truncate cursor-pointer", Icon ? "pl-8" : "pl-3")}
      >
        <option value="">{allLabel}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label || o.value}
            {o.count != null ? ` (${o.count})` : ""}
          </option>
        ))}
      </select>
      <ChevronDown className="w-3.5 h-3.5 absolute right-2.5 text-muted2 pointer-events-none" />
    </label>
  );
}

/** Filter chips. On phones they scroll sideways, unless `wrap` is set (a short set that fits on two lines). */
export function Chips({ items, value, onChange, wrap = false }) {
  return (
    <div className={cn("flex items-center gap-1.5 [&>*]:shrink-0", wrap ? "flex-wrap" : "max-md:flex-nowrap max-md:overflow-x-auto max-md:-mx-4 max-md:px-4 max-md:pb-1 md:flex-wrap")}>
      {items.map((c) => {
        const on = value === c.key;
        return (
          <button
            key={c.key}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(c.key)}
            className={cn(
              "inline-flex items-center gap-2 h-8 px-3 rounded-full text-[12.5px] font-semibold border transition-colors",
              on ? "bg-brand text-white border-brand" : "bg-card text-ink border-line hover:border-brand-ring"
            )}
          >
            {c.label}
            {c.count != null && <span className={cn("font-mono text-[11px]", on ? "text-white/80" : "text-muted2")}>{c.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
